import { useContext, useEffect, useState, ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, Controller, Control, UseFormSetValue } from 'react-hook-form';
import { Alert, DialogContent, MenuItem, Stack, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useGetProductsQuery } from '../../redux/services/products';
import {
  Application,
  ExternalFaceThickness,
  ExternalGrade,
  ExternalProfile,
  InternalFaceThickness,
  InternalGrade,
  InternalProfile,
  PanelThickness,
  PanelType,
  PanelWidthForFixingCalculations,
  ProductDetails,
  Fasteners,
} from 'types/domainTypes';
import { EditDialog } from 'components/EditDialog/EditDialog';
import { CalculationContext } from './CalculationContextProvider';
import { useAppSelector } from 'redux/hooks';
import { Division } from 'types/domainTypes';
import { Skeleton } from '@mui/material';

export type ProductDetailsFormData = {
  id: string;
  division: string;
  panelType: string;
  panelThickness: number | '';
  externalProfile: string;
  externalFaceThickness: number | '';
  externalGrade: string;
  internalProfile: string;
  internalFaceThickness: number | '';
  internalGrade: string;
  panelWidthForFixingCalculations: number | '';
};

const productDetailsFormDefaults: ProductDetailsFormData = {
  id: '',
  division: '',
  panelType: '',
  panelThickness: '',
  externalProfile: '',
  externalFaceThickness: '',
  externalGrade: '',
  internalProfile: '',
  internalFaceThickness: '',
  internalGrade: '',
  panelWidthForFixingCalculations: '',
};

type ProductOptionsProps = {
  name: keyof ProductDetails;
  label: string;
  options:
    | string[]
    | PanelType[]
    | PanelThickness[]
    | ExternalProfile[]
    | ExternalFaceThickness[]
    | ExternalGrade[]
    | InternalProfile[]
    | InternalFaceThickness[]
    | InternalGrade[]
    | PanelWidthForFixingCalculations[];
  control: Control<ProductDetailsFormData>;
  onClick: (fieldName: keyof ProductDetails, value: string | number) => void;
  onClickLastOption?: (lastOption: PanelWidthForFixingCalculations) => void;
  setValue: UseFormSetValue<ProductDetailsFormData>;
  disabled: boolean;
  isLoading?: boolean;
};

const KS_AMBER_WARNING_COLOR = '653D00';

function ProductOptions({
  name,
  label,
  options,
  control,
  onClick,
  onClickLastOption,
  setValue,
  disabled,
  isLoading,
}: ProductOptionsProps): ReactElement {
  const { t } = useTranslation();

  const setValueIfOnlyOption = (availableOptions: typeof options, fieldValue: string | number) => {
    if (availableOptions.length === 1 && typeof availableOptions[0] !== 'string') {
      if (fieldValue !== (availableOptions[0].value as typeof fieldValue)) {
        setValue(name, availableOptions[0].value as typeof fieldValue);
        if (name === 'panelWidthForFixingCalculations' && onClickLastOption) {
          const lastOption = availableOptions[0] as PanelWidthForFixingCalculations;
          onClickLastOption(lastOption);
        }
      }
    }
  };

  if (isLoading) {
    return <Skeleton height="60px" width="100%" />;
  }

  if (options.length === 0) {
    return (
      <Alert
        severity="error"
        sx={{
          backgroundColor: '#fdeded',
          '& .MuiAlert-icon': {
            color: '#d32f2f',
          },
        }}
        data-testid="product-details-dialog-no-data"
      >
        <Trans i18nKey="calculation-page.product-details-dialog.error-message-no-options-data">{{ option: label }}</Trans>
      </Alert>
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: true }}
      render={({ field, fieldState }) => {
        setValueIfOnlyOption(options, field.value);
        const helperText = fieldState.invalid ? t('common.required-error-text') : `*${t('common.required-helper-text')}`;
        return (
          <TextField
            select
            error={fieldState.invalid}
            onChange={field.onChange}
            value={field.value}
            helperText={helperText}
            label={label}
            disabled={disabled}
          >
            {options.map((option, index) => (
              <MenuItem
                key={index}
                value={typeof option === 'string' ? option : option.value}
                onClick={() => {
                  if (onClickLastOption) {
                    onClickLastOption(option as PanelWidthForFixingCalculations);
                  }

                  onClick(field.name, typeof option === 'string' ? option : option.value);
                }}
              >
                {typeof option === 'string' ? option : option.value}
              </MenuItem>
            ))}
          </TextField>
        );
      }}
    />
  );
}

const defaultPanelWidth: PanelWidthForFixingCalculations = {
  minEndSupportRoof: 0,
  minEndSupportWall: 0,
  minIntermediateSupportRoof: 0,
  minIntermediateSupportWall: 0,
  connectionStrength: 0,
  selfWeight: 0,
  value: 0,
  productId: '',
  productName: '',
  coreThickness: 0,
};

interface ProductDetailsDialogProps {
  onClose: () => void;
  onSave: (productDetails: ProductDetails, shouldDeleteFasteners: boolean) => Promise<void>;
  application: Application;
  productDetails?: ProductDetails;
  divisionData?: string[];
  fasteners?: Fasteners;
}

export function ProductDetailsDialog({
  onClose,
  onSave,
  application,
  productDetails,
  divisionData,
  fasteners,
}: ProductDetailsDialogProps): ReactElement {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [skip, setSkip] = useState(true);
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    clearErrors,
    formState: { isDirty },
  } = useForm<ProductDetailsFormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: productDetails || productDetailsFormDefaults,
  });

  const { setShouldShowFastenersTooltip } = useContext(CalculationContext);
  const [filteredProduct, setFilteredProduct] = useState<PanelWidthForFixingCalculations>(defaultPanelWidth);

  const selected = {
    division: watch('division'),
    panelType: watch('panelType'),
    panelThickness: watch('panelThickness'),
    externalProfile: watch('externalProfile'),
    externalFaceThickness: watch('externalFaceThickness'),
    externalGrade: watch('externalGrade'),
    internalProfile: watch('internalProfile'),
    internalFaceThickness: watch('internalFaceThickness'),
    internalGrade: watch('internalGrade'),
    panelWidthForFixingCalculations: watch('panelWidthForFixingCalculations'),
  };

  const {
    data: productData,
    isLoading,
    isError,
  } = useGetProductsQuery(
    {
      panelType: application,
      divisionName: selected.division,
    },
    { skip }
  );
  const panelThicknessOptions = productData?.find((panelType) => panelType.value === selected.panelType)?.panelThicknesses || [];

  const externalProfileOptions =
    panelThicknessOptions.find((panelThickness) => panelThickness.value === selected.panelThickness)?.externalProfiles || [];
  const externalFaceThicknessOptions =
    externalProfileOptions.find((externalProfile) => externalProfile.value === selected.externalProfile)?.externalFaceThicknesses || [];
  const externalGradeOptions =
    externalFaceThicknessOptions.find((externalFaceThickness) => externalFaceThickness.value === selected.externalFaceThickness)
      ?.externalGrades || [];

  const internalProfileOptions =
    externalGradeOptions.find((externalGrade) => externalGrade.value === selected.externalGrade)?.internalProfiles || [];
  const internalFaceThicknessOptions =
    internalProfileOptions.find((internalProfile) => internalProfile.value === selected.internalProfile)?.internalFaceThicknesses || [];
  const internalGradeOptions =
    internalFaceThicknessOptions.find((internalFaceThickness) => internalFaceThickness.value === selected.internalFaceThickness)
      ?.internalGrades || [];

  // const coverWidthOptions = internalGradeOptions.find((internalGrade) => internalGrade.value === selected.internalGrade)?.coverWidths || [];
  const division: Division = useAppSelector((state) => state.user.division);

  useEffect(() => {
    if (division === Division.Kingspan) {
      setValue('division', 'Kingspan UK/Ire');
    } else if (division === Division.Joriside) {
      setValue('division', 'Joris Ide');
    }
  }, [division, setValue]);

  const panelWidthsForFixingCalculationsOptions =
    internalGradeOptions.find((internalGrade) => internalGrade.value === selected.internalGrade)?.panelWidthsForFixingCalculations || [];

  useEffect(() => {
    if (selected.division) {
      setSkip(false);
    }
  }, [t, enqueueSnackbar, selected.division]);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t('calculation-page.product-details-dialog.error-message-load-product'), {
        variant: 'error',
      });
    }
  }, [t, enqueueSnackbar, isError]);

  const onSaveHandler = async (formData: ProductDetailsFormData) => {
    const convertedFormData = {
      id: filteredProduct.productId,
      division: formData.division,
      panelType: formData.panelType,
      panelThickness: formData.panelThickness as number,
      externalProfile: formData.externalProfile,
      externalFaceThickness: formData.externalFaceThickness as number,
      externalGrade: formData.externalGrade,
      internalProfile: formData.internalProfile,
      internalFaceThickness: formData.internalFaceThickness as number,
      internalGrade: formData.internalGrade,
      panelWidthForFixingCalculations: formData.panelWidthForFixingCalculations as number,
      // non form fields
      minEndSupportWall: filteredProduct.minEndSupportWall as number,
      minIntermediateSupportWall: filteredProduct.minIntermediateSupportWall as number,
      minEndSupportRoof: filteredProduct.minEndSupportRoof as number,
      minIntermediateSupportRoof: filteredProduct.minIntermediateSupportRoof as number,
      connectionStrength: filteredProduct.connectionStrength as number,
      selfWeight: filteredProduct.selfWeight as number,
      coreThickness: filteredProduct.coreThickness as number,
    };

    let isPanelTypeChanged,
      isExternalFaceThicknessChanged = false;
    if (productDetails) {
      isPanelTypeChanged = productDetails.panelType !== formData.panelType;
      isExternalFaceThicknessChanged = productDetails.externalFaceThickness !== (formData.externalFaceThickness as number);
    }
    const shouldDeleteFasteners = isPanelTypeChanged || isExternalFaceThicknessChanged;
    if (fasteners) setShouldShowFastenersTooltip(true);
    await onSave(convertedFormData, shouldDeleteFasteners);
  };

  const clearSubsequentFields = (fieldName: keyof ProductDetails) => {
    const fieldNames: (keyof ProductDetails)[] = [
      'division',
      'panelType',
      'panelThickness',
      'externalProfile',
      'externalFaceThickness',
      'externalGrade',
      'internalProfile',
      'internalFaceThickness',
      'internalGrade',
      'panelWidthForFixingCalculations',
    ];
    const idx = fieldNames.findIndex((field) => field === fieldName);
    for (let i = idx + 1; i < fieldNames.length; i++) {
      setValue(fieldNames[i], '');
    }
  };

  const onClick = (fieldName: keyof ProductDetails, value: string | number) => {
    clearErrors();
    if (fieldName !== 'id' && selected[fieldName] !== value) {
      clearSubsequentFields(fieldName);
    }
  };

  const handleClickLastOption = (lastOption: PanelWidthForFixingCalculations) => {
    setFilteredProduct(lastOption);
  };

  const showFastenersWarning: JSX.Element = (
    <Alert severity="warning" sx={{ m: 2 }}>
      <span data-testid="alert-message-title" style={{ color: KS_AMBER_WARNING_COLOR, fontWeight: 'bold' }}>
        {t('calculation-page.fasteners-details-dialog.clearing-existing-fastener-warning-title')}
      </span>
      <br />
      <span
        data-testid="alert-message"
        style={{
          color: KS_AMBER_WARNING_COLOR,
          fontWeight: 'normal',
          fontSize: '14px',
        }}
      >
        {t('calculation-page.fasteners-details-dialog.clearing-existing-fastener-warning-message')}
      </span>
    </Alert>
  );

  return (
    <EditDialog
      title={productDetails ? t('calculation-page.product-details-dialog.edit-title') : t('calculation-page.product-details-dialog.title')}
      saveActionTestId="product-dialog-add-product-button"
      saveActionText={
        productDetails
          ? t('calculation-page.product-details-dialog.save-edit-action-text')
          : t('calculation-page.product-details-dialog.save-add-action-text')
      }
      isDirty={isDirty}
      onClose={onClose}
      handleFormSubmit={handleSubmit(onSaveHandler)}
    >
      <DialogContent dividers sx={{ padding: 1, display: 'flex', flexDirection: 'column' }}>
        {productDetails && fasteners?.manufacturerName && fasteners?.fastenerMaterial && showFastenersWarning}
        <Stack sx={{ padding: 2 }} spacing={3}>
          <ProductOptions
            name="division"
            label={t('calculation-page.product-details-dialog.label-division')}
            options={divisionData || []}
            control={control}
            onClick={onClick}
            setValue={setValue}
            disabled={!!selected.division}
          />
          {selected.division && (
            <ProductOptions
              name="panelType"
              label={t('calculation-page.product-details-dialog.label-panel-type')}
              options={productData || []}
              isLoading={isLoading}
              control={control}
              onClick={onClick}
              setValue={setValue}
              disabled={false}
            />
          )}
          {selected.panelType && (
            <ProductOptions
              name="panelThickness"
              label={t('calculation-page.product-details-dialog.label-panel-thickness')}
              options={panelThicknessOptions}
              isLoading={isLoading}
              control={control}
              onClick={onClick}
              setValue={setValue}
              disabled={false}
            />
          )}
          {selected.panelThickness && (
            <ProductOptions
              name="externalProfile"
              label={t('calculation-page.product-details-dialog.label-external-profile')}
              options={externalProfileOptions}
              isLoading={isLoading}
              control={control}
              onClick={onClick}
              setValue={setValue}
              disabled={false}
            />
          )}
          {selected.externalProfile && (
            <ProductOptions
              name="externalFaceThickness"
              label={t('calculation-page.product-details-dialog.label-external-face-thickness')}
              options={externalFaceThicknessOptions}
              isLoading={isLoading}
              control={control}
              onClick={onClick}
              setValue={setValue}
              disabled={false}
            />
          )}
          {selected.externalFaceThickness && (
            <ProductOptions
              name="externalGrade"
              label={t('calculation-page.product-details-dialog.label-external-grade')}
              options={externalGradeOptions}
              isLoading={isLoading}
              control={control}
              onClick={onClick}
              setValue={setValue}
              disabled={false}
            />
          )}
          {selected.externalGrade && (
            <ProductOptions
              name="internalProfile"
              label={t('calculation-page.product-details-dialog.label-internal-profile')}
              options={internalProfileOptions}
              isLoading={isLoading}
              control={control}
              onClick={onClick}
              setValue={setValue}
              disabled={false}
            />
          )}
          {selected.internalProfile && (
            <ProductOptions
              name="internalFaceThickness"
              label={t('calculation-page.product-details-dialog.label-internal-face-thickness')}
              options={internalFaceThicknessOptions}
              isLoading={isLoading}
              control={control}
              onClick={onClick}
              setValue={setValue}
              disabled={false}
            />
          )}
          {selected.internalFaceThickness && (
            <ProductOptions
              name="internalGrade"
              label={t('calculation-page.product-details-dialog.label-internal-grade')}
              options={internalGradeOptions}
              isLoading={isLoading}
              control={control}
              onClick={onClick}
              setValue={setValue}
              disabled={false}
            />
          )}
          {selected.internalGrade && (
            <ProductOptions
              name="panelWidthForFixingCalculations"
              label={t('calculation-page.product-details-dialog.label-cover-width')}
              options={panelWidthsForFixingCalculationsOptions}
              isLoading={isLoading}
              control={control}
              onClick={onClick}
              onClickLastOption={handleClickLastOption}
              setValue={setValue}
              disabled={false}
            />
          )}
        </Stack>
      </DialogContent>
    </EditDialog>
  );
}
